import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Coffee, Wind, MessageCircle, Heart, ArrowRight, Sparkles, 
  Calendar, Clock, Target, CheckCircle, Star, Lightbulb,
  Shield, Users, Brain, Award
} from 'lucide-react';
import { sexualHealthData } from './SexualHealthData';

const DailyTipsCarousel = () => {
  const [currentSexualHealthIndex, setCurrentSexualHealthIndex] = useState(0);
  const [currentConsentIndex, setCurrentConsentIndex] = useState(0);
  const [currentAwarenessIndex, setCurrentAwarenessIndex] = useState(0);

  const [activeTipSection, setActiveTipSection] = useState('overview');

  const renderDailyTips = () => {
    const nextSexualHealth = () => {
      setCurrentSexualHealthIndex((prev) => 
        (prev + 1) % sexualHealthData.dailyTips.sexualHealth.length
      );
    };

    const prevSexualHealth = () => {
      setCurrentSexualHealthIndex((prev) => 
        (prev - 1 + sexualHealthData.dailyTips.sexualHealth.length) % sexualHealthData.dailyTips.sexualHealth.length
      );
    };

    const nextConsent = () => {
      setCurrentConsentIndex((prev) => 
        (prev + 1) % sexualHealthData.dailyTips.consent.length
      );
    };

    const prevConsent = () => {
      setCurrentConsentIndex((prev) => 
        (prev - 1 + sexualHealthData.dailyTips.consent.length) % sexualHealthData.dailyTips.consent.length
      );
    };

    const nextAwareness = () => {
      setCurrentAwarenessIndex((prev) => 
        (prev + 1) % sexualHealthData.dailyTips.awareness.length
      );
    };

    const prevAwareness = () => {
      setCurrentAwarenessIndex((prev) => 
        (prev - 1 + sexualHealthData.dailyTips.awareness.length) % sexualHealthData.dailyTips.awareness.length
      );
    };

    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Daily Wellness Tips</h2>
              <p className="text-xs sm:text-sm text-gray-600">Transform your health with simple practices</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
          {[
            { 
              id: "sexual-health",
              title: "Sexual Health", 
              icon: Target, 
              color: "blue", 
              description: "Daily practices for wellness",
              count: sexualHealthData.dailyTips.sexualHealth.length,
            },
            { 
              id: "consent",
              title: "Consent & Communication", 
              icon: Heart, 
              color: "purple", 
              description: "Building trust and respect",
              count: sexualHealthData.dailyTips.consent.length,
            },
            { 
              id: "awareness",
              title: "Self-Awareness", 
              icon: Star, 
              color: "pink", 
              description: "Mindfulness practices",
              count: sexualHealthData.dailyTips.awareness.length,
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveTipSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'blue' ? 'border-blue-500 hover:border-blue-600' :
                item.color === 'purple' ? 'border-purple-500 hover:border-purple-600' :
                item.color === 'pink' ? 'border-pink-500 hover:border-pink-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'blue' ? 'text-blue-600' :
                item.color === 'purple' ? 'text-purple-600' :
                item.color === 'pink' ? 'text-pink-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                item.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                item.color === 'pink' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} tips
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeTipSection === 'overview' && renderOverview()}
        
        {activeTipSection === 'sexual-health' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveTipSection('overview')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-4"
            >
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Sexual Health Practices</h3>
                <p className="text-gray-600 text-sm">Daily practices that transform your wellness</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 shadow-lg border border-blue-200">
                <div className="flex justify-between items-center mb-4">
                  <button 
                    onClick={prevSexualHealth}
                    className="bg-white shadow-md rounded-full p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </button>
                  
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">
                      {currentSexualHealthIndex + 1} of {sexualHealthData.dailyTips.sexualHealth.length}
                    </div>
                    <div className="flex gap-1">
                      {sexualHealthData.dailyTips.sexualHealth.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSexualHealthIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentSexualHealthIndex 
                              ? 'bg-blue-600 w-6' 
                              : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={nextSexualHealth}
                    className="bg-white shadow-md rounded-full p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSexualHealthIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6"
                  >
                    {(() => {
                      const tip = sexualHealthData.dailyTips.sexualHealth[currentSexualHealthIndex];
                      
                      return (
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                              <Target className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-gray-800">Daily Health Tip #{currentSexualHealthIndex + 1}</h4>
                              <p className="text-blue-600 text-sm font-medium">Sexual Health</p>
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                              <h5 className="text-blue-800 font-semibold text-sm mb-2">✨ Today's Practice</h5>
                              <p className="text-blue-700 text-sm">{tip.tip}</p>
                            </div>
                            
                            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                              <h5 className="text-green-800 font-semibold text-sm mb-2">💡 Why It Works</h5>
                              <p className="text-green-700 text-sm">{tip.benefit}</p>
                            </div>
                            
                            <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-4">
                              <h5 className="text-purple-800 font-semibold text-sm mb-2">🎯 How To Do It</h5>
                              <p className="text-purple-700 text-sm">{tip.howTo}</p>
                            </div>
                            
                            <div className="text-center">
                              <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full">
                                <Target className="w-4 h-4 text-gray-600" />
                                <span className="text-xs text-gray-700 font-medium">Health & Wellness Tip</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}

        {activeTipSection === 'consent' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveTipSection('overview')}
                className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4"
            >
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🤝 Consent & Communication</h3>
                <p className="text-gray-600 text-sm">Building trust and healthy communication</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 shadow-lg border border-purple-200">
                <div className="flex justify-between items-center mb-4">
                  <button 
                    onClick={prevConsent}
                    className="bg-white shadow-md rounded-full p-2 text-purple-600 hover:bg-purple-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </button>
                  
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">
                      {currentConsentIndex + 1} of {sexualHealthData.dailyTips.consent.length}
                    </div>
                    <div className="flex gap-1">
                      {sexualHealthData.dailyTips.consent.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentConsentIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentConsentIndex 
                              ? 'bg-purple-600 w-6' 
                              : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={nextConsent}
                    className="bg-white shadow-md rounded-full p-2 text-purple-600 hover:bg-purple-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentConsentIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6"
                  >
                    {(() => {
                      const tip = sexualHealthData.dailyTips.consent[currentConsentIndex];
                      
                      return (
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                              <Heart className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-gray-800">Consent Tip #{currentConsentIndex + 1}</h4>
                              <p className="text-purple-600 text-sm font-medium">Consent & Communication</p>
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-4">
                              <h5 className="text-purple-800 font-semibold text-sm mb-2">💬 Today's Practice</h5>
                              <p className="text-purple-700 text-sm">{tip.tip}</p>
                            </div>
                            
                            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                              <h5 className="text-blue-800 font-semibold text-sm mb-2">🧠 Understanding</h5>
                              <p className="text-blue-700 text-sm">{tip.benefit}</p>
                            </div>
                            
                            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                              <h5 className="text-green-800 font-semibold text-sm mb-2">🌟 How To Do It</h5>
                              <p className="text-green-700 text-sm">{tip.howTo}</p>
                            </div>
                            
                            <div className="text-center">
                              <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full">
                                <Heart className="w-4 h-4 text-gray-600" />
                                <span className="text-xs text-gray-700 font-medium">Consent & Communication</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}

        {activeTipSection === 'awareness' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveTipSection('overview')}
                className="flex items-center gap-2 text-pink-600 hover:text-pink-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-4"
            >
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🧘 Self-Awareness & Mindfulness</h3>
                <p className="text-gray-600 text-sm">Develop deeper self-understanding and mindful practices</p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6 shadow-lg border border-pink-200">
                <div className="flex justify-between items-center mb-4">
                  <button 
                    onClick={prevAwareness}
                    className="bg-white shadow-md rounded-full p-2 text-pink-600 hover:bg-pink-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </button>
                  
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">
                      {currentAwarenessIndex + 1} of {sexualHealthData.dailyTips.awareness.length}
                    </div>
                    <div className="flex gap-1">
                      {sexualHealthData.dailyTips.awareness.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentAwarenessIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentAwarenessIndex 
                              ? 'bg-pink-600 w-6' 
                              : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={nextAwareness}
                    className="bg-white shadow-md rounded-full p-2 text-pink-600 hover:bg-pink-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentAwarenessIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6"
                  >
                    {(() => {
                      const tip = sexualHealthData.dailyTips.awareness[currentAwarenessIndex];
                      
                      return (
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                              <Star className="w-6 h-6 text-pink-600" />
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-gray-800">Awareness Tip #{currentAwarenessIndex + 1}</h4>
                              <p className="text-pink-600 text-sm font-medium">Self-Awareness</p>
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            <div className="bg-pink-50 border-l-4 border-pink-500 rounded-lg p-4">
                              <h5 className="text-pink-800 font-semibold text-sm mb-2">🎯 Today's Practice</h5>
                              <p className="text-pink-700 text-sm">{tip.tip}</p>
                            </div>
                            
                            <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                              <h5 className="text-indigo-800 font-semibold text-sm mb-2">🔍 Why It Works</h5>
                              <p className="text-indigo-700 text-sm">{tip.benefit}</p>
                            </div>
                            
                            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                              <h5 className="text-green-800 font-semibold text-sm mb-2">✨ How To Do It</h5>
                              <p className="text-green-700 text-sm">{tip.howTo}</p>
                            </div>
                            
                            <div className="text-center">
                              <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full">
                                <Star className="w-4 h-4 text-gray-600" />
                                <span className="text-xs text-gray-700 font-medium">Self-Awareness & Mindfulness</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}

     

      </div>
    );
  };

  return renderDailyTips();
};

export default DailyTipsCarousel;